import axios from 'axios';

// Relative, so one build serves both app.rss.solar and manage.rss.solar.
const API_URL = import.meta.env.VITE_API_URL || '/api';

/**
 * Create a custom axios instance with parameter serialization and cookie support
 */
const api = axios.create({
  withCredentials: true // Allow cookies to be sent with all requests
});

/**
 * Get CSRF token from document cookie
 * @returns {string|null} The CSRF token or null if not found
 */
export const getCsrfToken = () => {
  // Extract CSRF token from cookie
  const match = document.cookie.match(/XSRF-TOKEN=([^;]+)/);
  return match ? match[1] : null;
};

/**
 * Make a GET request with cookie-based authentication
 * @param {string} url - The URL to fetch from
 * @param {object} options - Request options or params object
 * @returns {Promise} Axios response
 */
export const get = async (url, options = {}) => {
  // Handle the case where options is actually just the params object
  // This ensures backward compatibility with existing code
  let axiosOptions = {};
  
  if (options && typeof options === 'object') {
    // If options is an object but not clearly a params object or axiosOptions object,
    // assume it's params for backward compatibility
    if (!options.hasOwnProperty('params') && !options.hasOwnProperty('headers')) {
      axiosOptions.params = options;
    } else {
      // It's already in the correct format
      axiosOptions = { ...options };
    }
  }

  // Ensure withCredentials is set
  axiosOptions.withCredentials = true;
  
  return api.get(url, axiosOptions);
};

/**
 * Make a POST request with cookie-based authentication
 * @param {string} url - The URL to post to
 * @param {object} data - The data to post
 * @returns {Promise} Axios response
 */
export const post = async (url, data = {}) => {
  // Get CSRF token if available
  const csrfToken = getCsrfToken();
  const headers = {
    'Content-Type': 'application/json'
  };
  
  // Add CSRF token header if available
  if (csrfToken) {
    headers['X-XSRF-TOKEN'] = csrfToken;
  }
  
  return api.post(url, data, {
    headers,
    withCredentials: true
  });
};

/**
 * Make a PUT request with cookie-based authentication
 * @param {string} url - The URL to put to
 * @param {object} data - The data to put
 * @returns {Promise} Axios response
 */
export const put = async (url, data = {}) => {
  // Get CSRF token if available
  const csrfToken = getCsrfToken();
  const headers = {
    'Content-Type': 'application/json'
  };
  
  // Add CSRF token header if available
  if (csrfToken) {
    headers['X-XSRF-TOKEN'] = csrfToken;
  }
  
  return api.put(url, data, {
    headers,
    withCredentials: true
  });
};

/**
 * Make a DELETE request with cookie-based authentication
 * @param {string} url - The URL to delete from
 * @returns {Promise} Axios response
 */
export const remove = async (url) => {
  // Get CSRF token if available
  const csrfToken = getCsrfToken();
  const headers = {};
  
  // Add CSRF token header if available
  if (csrfToken) {
    headers['X-XSRF-TOKEN'] = csrfToken;
  }
  
  return api.delete(url, {
    headers,
    withCredentials: true
  });
};

export { API_URL, api }; 